import { useState } from 'react';

import Link from 'next/link';

import { useMediaQuery, Grid, Typography, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { RichText } from 'prismic-reactjs';
import PropTypes from 'prop-types';

import { Icon } from '@/src/components/atoms';
import { SectionHeader } from '@/src/components/molecules';
import { CardBase, CardPricingStandard } from '@/src/components/organisms';

import SectionDynamic from './SectionDynamic';

// Custom styles
const useStyles = makeStyles(theme => ({
  container: {
    marginBottom: theme.spacing(3),
  },
  fontWeightBold: {
    fontWeight: 'bold',
  },
}));

export default function Pricings(props) {
  const { className, section, title, subtitle, pricingGroups = [], refElem, ...rest } = props;

  // Hooks
  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  // States
  const [option, setOption] = useState(pricingGroups[0] || null);

  return (
    <SectionDynamic type={section}>
      <div className={className} ref={refElem} {...rest}>
        <SectionHeader title={title} subtitle={subtitle} data-aos="fade-up" />
        {pricingGroups.length > 0 && (
          <Grid container spacing={isMd ? 4 : 2} className={classes.container}>
            {pricingGroups.map((pricingGroup, index) => (
              <Grid key={pricingGroup.title || index.toString()} item xs={12} md={6}>
                <CardBase withShadow liftUp>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={9}>
                      <Typography
                        variant="h5"
                        color="textPrimary"
                        gutterBottom
                        className={classes.fontWeightBold}>
                        {pricingGroup.title}
                      </Typography>
                      <Typography variant="body1" color="textPrimary">
                        {pricingGroup.subtitle}
                      </Typography>
                    </Grid>
                    <Grid item container justify="flex-end" alignItems="center" xs={12} sm={3}>
                      <Button
                        aria-label={`Escolher ${pricingGroup.title}`}
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth
                        onClick={() => setOption(pricingGroup)}
                        disabled={option.pricing_group?.uid === pricingGroup.pricing_group?.uid}>
                        Escolher
                      </Button>
                    </Grid>
                  </Grid>
                </CardBase>
              </Grid>
            ))}
          </Grid>
        )}
        {option && (
          <Grid container spacing={isMd ? 4 : 2} justifyContent="center">
            {option.pricing_group?.data?.prices?.map(item => {
              const { price = {} } = item;
              return (
                <Grid item xs={12} md={4} data-aos="fade-up" key={price.uid}>
                  <div>
                    <CardPricingStandard
                      withShadow
                      title={price.data?.title}
                      liftUp
                      subtitle={price.data?.subtitle}
                      priceComponent={
                        !price.data?.price ? null : (
                          <div>
                            <Typography variant="h3" component="span">
                              {price.data?.price}
                            </Typography>
                            <Typography component="span" variant="subtitle1">
                              {' '}
                              / {price.data?.suffix}
                            </Typography>
                            {price.data?.setup_fee && (
                              <Typography variant="subtitle2" component="div" color="textSecondary">
                                {price.data?.setup_fee}
                              </Typography>
                            )}
                          </div>
                        )
                      }
                      features={RichText.asText(price.data?.features).split('\n') || []}
                      featureCheckComponent={
                        <Icon
                          fontIconClass="far fa-check-circle"
                          fontIconColor={theme.palette.primary.main}
                        />
                      }
                      cta={
                        <Link
                          href="/schedule?utm_campaign=solutions-details&utm_content=pricings"
                          passHref>
                          <Button
                            component="a"
                            color="primary"
                            variant={price.data?.ishighlighted ? 'contained' : 'outlined'}
                            fullWidth
                            size="large"
                            aria-label="Agendar demonstração">
                            Agendar demonstração
                          </Button>
                        </Link>
                      }
                      disclaimer={price.data?.disclaimer}
                    />
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </div>
    </SectionDynamic>
  );
}

Pricings.propTypes = {
  className: PropTypes.string,
  section: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  pricingGroups: PropTypes.array,
  refElem: PropTypes.any,
};
